import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/Main'
import { Button, Form } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import SelectWrapper from 'shared/components/SelectWrapper'
import resAddSection from './resolvers/resAddSection'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from 'shared/utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import { apiUrls, iconOptionsList } from 'consts'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import BackButton from 'shared/components/BackButton'
import AddRemoveTableSortable from 'components/AddRemoveTableSortable'
import AddPage from 'pages/AddPage'
import Entity from 'components/AddRemoveTableSortable/types/Entity'
import ModalEditableProps from 'components/EditModal/types/ModalEditableProps'
import prepareSubmitFormFromModal from 'components/EditModal/utils/prepareSubmitFormFromModal'

const submitRef = React.createRef<HTMLButtonElement>()

const AddSection: React.FunctionComponent<ModalEditableProps> = React.forwardRef(
  (props: ModalEditableProps, ref) => {
    const [showOverlay, setShowOverlay] = useState<boolean>(true)
    const [dataLoaded, setDataLoaded] = useState<boolean>(false)

    const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})
    const [relationalPages, setRelationalPages] = useState<Array<Entity>>([])

    const { id } = useParams()

    const navigate = useNavigate()
    const [sectionId] = useState<string | undefined>(props.modalConfig?.entityId ?? id)

    const [colAttributes] = useState<any>(props.modalConfig !== undefined ? { md: 12 } : undefined)

    const {
      control,
      register,
      getValues,
      handleSubmit,
      reset,
      setFocus,
      setError,
      setValue,
      formState: { errors },
    } = useForm({
      mode: 'all',
      resolver: resAddSection,
    })

    useInit({
      getValues,
      id: sectionId,
      reset,
      setDataLoaded,
      setShowOverlay,
      currentCustomer,
      setValue
    })

    useFocusError({
      errors,
      setFocus,
    })

    const renderView = (innerContent: any) => {
      return props.modalConfig === undefined ? (
        <Layout
          footerContent={<Footer />}
          brandContent={getBrandContent()}
          showOverlay={showOverlay}
          showCardHeader={true}
          headerInsert={<BackButton url='/sections' text='All Sections' />}
          actionInsert={
            <Button
              disabled={!dataLoaded}
              type='submit'
              name='save'
              className='btn-round btn-primary'
              onClick={() => {
                if (submitRef.current != null) {
                  submitRef.current.click()
                }
              }}
            >
              Save Section
            </Button>
          }
        >
          {innerContent}
        </Layout>
      ) : (
        innerContent
      )
    }

    return renderView(
      <>
        <Form
          onSubmit={handleSubmit((data) => {
            const commonParams = {
              submitForm,
              data,
              setShowOverlay,
              setError,
              id: sectionId,
              name: 'Section',
              successNav: '/sections',
              extraData: {
                pages: relationalPages,
              },
            }

            return props.modalConfig !== undefined
              ? prepareSubmitFormFromModal({
                  ...commonParams,
                  modalConfig: props.modalConfig,
                })
              : prepareSubmitForm({
                  ...commonParams,
                  navigate: navigate,
                })
          })}
          id='main-form'
        >
          <InputWrapper colAttributes={colAttributes} label='Name' name='name' errors={errors}>
            <Form.Control type='text' {...register('name')} />
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='Superadmin Only' name='superadmin_only' errors={errors}>
            <Form.Select {...register('superadmin_only')}>
              <option key={0} value={0}>
                No
              </option>
              <option key={1} value={1}>
                Yes
              </option>
            </Form.Select>
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='Active' name='active' errors={errors}>
            <Form.Select {...register('active')}>
              <option key={0} value={1}>
                Yes
              </option>
              <option key={1} value={0}>
                No
              </option>
            </Form.Select>
          </InputWrapper>

          <InputWrapper label='Icon' name='icon' errors={errors} colAttributes={{ md: 6 }}>
            <SelectWrapper
              name='icon'
              control={control}
              options={iconOptionsList}
              optionLabel='name'
              optionValue='id'
              placeholder='Select Icon...'
            />
          </InputWrapper>

          <Button type='submit' name='save' ref={submitRef} className='d-none'></Button>

          <Button
            type='submit'
            name='save'
            ref={(ref as any) ?? submitRef}
            className='d-none'
          ></Button>
        </Form>

        {props.modalConfig === undefined && (
          <AddRemoveTableSortable
            getAllRowsUrl={apiUrls.sectionsToPages + '?id=' + id}
            addRemoveRowUrl={apiUrls.sectionsToPages}
            relationalFieldName='page_id'
            toggleActiveUrl={apiUrls.pages}
            maxTableHeight='600px'
            entityNameSingular='Page'
            entityNamePlural='Pages'
            sortRowsUrl={apiUrls.sectionsToPages + '/' + apiUrls.actionSort}
            setShowOverlay={setShowOverlay}
            editFormComponent={AddPage}
            onAddItem={
              id !== undefined
                ? undefined
                : (entity: Entity) => {
                    setRelationalPages((prevState) => [...prevState, entity])
                    return true
                  }
            }
            onRemoveItem={
              id !== undefined
                ? undefined
                : (entity: Entity) => {
                    setRelationalPages((prevState) => prevState.filter((r) => r.id !== entity.id))
                    return true
                  }
            }
          />
        )}
      </>,
    )
  },
)

export default AddSection
